import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NodeAvatar } from '@/components/nodes/NodeAvatar'
import { InputPassword, Input } from '@/components/ui/input'
import { AuthLayout, AuthFormActions, AuthFooterLinks } from '@/components/auth'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/useAuth'
import { loginSchema, LoginFormValues } from '@/lib/validations/auth'
import { DEMO_ACCOUNTS } from '@/lib/seed-data/demo-helper'
import { resetStorage } from '@/services/init.service'
import { ChevronDown } from 'lucide-react'
import type { User } from '@/types/nodes'
import { PASSWORD_PLACEHOLDER } from '@/components/ui/input'

export default function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  // Reset storage when visiting login page to ensure clean, fresh data
  useEffect(() => {
    resetStorage()
  }, [])

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  })

  const loadDemoAccount = (accountId: 'keenan' | 'jaime') => {
    const account = DEMO_ACCOUNTS.find((acc) => acc.id === accountId)
    if (!account) return

    // Auto-fill with selected demo account credentials
    setValue('email', account.email, { shouldValidate: true, shouldDirty: true })
    setValue('password', account.password, { shouldValidate: true, shouldDirty: true })

    // Auto-submit the form after a brief delay to allow form validation
    setTimeout(() => {
      handleSubmit(onSubmit)()
    }, 300)

    toast.info(`Logging in as ${account.name}...`)
  }

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true)
    try {
      await login(data)
      toast.success('Welcome back!')
      navigate('/')
    } catch (error) {
      console.error('Login error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to log in')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout title="Log In" description="Enter your credentials to access your account">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            {...register('email')}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium">
            Password
          </label>
          <InputPassword
            id="password"
            placeholder={PASSWORD_PLACEHOLDER}
            error={errors.password?.message}
            autoComplete="current-password"
            {...register('password')}
          />
        </div>

        <AuthFormActions gap={3}>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Log In'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className="w-full" disabled={isLoading}>
                Use Demo Account
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {DEMO_ACCOUNTS.map((account) => (
                <DropdownMenuItem
                  key={account.id}
                  onClick={() => loadDemoAccount(account.id)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-3 w-full">
                    <NodeAvatar
                      node={{ name: account.name, avatar: account.avatar } as User}
                      size="md"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{account.name}</span>
                      <span className="text-xs text-muted-foreground">{account.email}</span>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <AuthFooterLinks
            links={[
              { to: '/forgot-password', label: 'Forgot password?' },
              { to: '/signup', label: 'Sign up' },
            ]}
          />
        </AuthFormActions>
      </form>
    </AuthLayout>
  )
}
